/*
 * Purpose: Provide lightweight transport utilities for JSON and binary HTTP requests.
 * Boundary: Service-layer utility only; no business logic; used by API service modules.
 * Invariants:
 * - Automatically applies JSON headers and base path normalization.
 * - Throws standardized Error objects for non-OK responses.
 * - ASCII only; no Unicode in comments or strings.
 */

// Constants - base URL used when none provided
const defaultBase = "/";

// Helper Function getXsrfToken:
// Reads XSRF-TOKEN cookie for anti-forgery double-submit pattern
function getXsrfToken() {
    var m = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/);
    return m ? decodeURIComponent(m[1]) : null;
}

// Helper Function jsonHeaders:
// Returns JSON header set depending on body presence, includes XSRF token
function jsonHeaders(hasBody) {
    var h = hasBody
        ? { "Content-Type": "application/json", "Accept": "application/json" }
        : { "Accept": "application/json" };
    var x = getXsrfToken();
    if (x) h["X-XSRF-TOKEN"] = x;
    return h;
}

// Helper Function join:
// Combine base and relative path safely
function join(base, url) {
    if (!base) return url;
    const b = base.endsWith("/") ? base.slice(0, -1) : base;
    const u = url.startsWith("/") ? url : "/" + url;
    return b + u;
}

// Function request:
// Core fetch wrapper with JSON parsing, error handling, and base URL support
async function request(url, { method = "GET", body, headers, base = defaultBase } = {}) {
    const res = await fetch(join(base, url), {
        method,
        headers: { ...jsonHeaders(!!body), ...headers },
        body: body ? JSON.stringify(body) : undefined,
        credentials: "same-origin",
    });

    // NOTE: 401 handling is done at the middleware level (GuestTokenRenewalMiddleware)
    // for guest-path endpoints, not here. See: logout-revisions-v2.md

    // Response Parsing - try JSON, fallback to plain text
    const text = await res.text();
    let data = null;
    try {
        data = text ? JSON.parse(text) : null;
    } catch {
        data = text || null;
    }

    // Error Handling - throw Error with status and payload
    if (!res.ok) {
        const err = new Error((data && data.message) || res.statusText || `HTTP ${res.status}`);
        err.status = res.status;
        err.data = data;
        throw err;
    }

    return data;
}

// Function postJSON:
// POST helper that serializes JSON payload automatically
async function postJSON(url, dto) {
    return request(url, { method: "POST", body: dto });
}

// Function getBlob:
// Fetch raw binary data using same-origin credentials
async function getBlob(url) {
    const res = await fetch(join(defaultBase, url), { credentials: "same-origin" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.blob();
}

// Function withBase:
// Returns a transport object scoped to a specific base URL
function withBase(base) {
    return {
        postJSON: (u, dto) => request(u, { method: "POST", body: dto, base }),
        getBlob: (u) =>
            fetch(join(base, u), { credentials: "same-origin" }).then((r) => {
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                return r.blob();
            }),
        request: (u, opts) => request(u, { ...opts, base }),
    };
}

// Export - default transport API
export default { postJSON, getBlob, withBase, request };
